export type CoverGeometryType = "perfect_binding" | "casewrap";

export type CoverGeometryInput = {
  panelWmm: number;
  panelHmm: number;
  spineMm: number;
  coverGeometryType?: CoverGeometryType | string | null;
  coverWrapMm?: number | string | null;
  coverSqueezeMm?: number | string | null;
  coverOverlapMm?: number | string | null;
};

export type CoverBoxMm = {
  x: number;
  y: number;
  w: number;
  h: number;
};

export type CoverSpreadGeometry = {
  type: CoverGeometryType;
  panelWmm: number;
  panelHmm: number;
  spineMm: number;
  wrapMm: number;
  squeezeMm: number;
  overlapMm: number;
  edgeMm: number;
  artWmm: number;
  artHmm: number;
  back: CoverBoxMm;
  spine: CoverBoxMm;
  front: CoverBoxMm;
  wrapBox: CoverBoxMm | null;
  contentBox: CoverBoxMm;
};

export function inferCoverGeometryType(args: {
  coverGeometryType?: string | null;
  coverThicknessType?: string | null;
}): CoverGeometryType {
  const thickness = String(args.coverThicknessType || "");
  if (thickness === "hardcover_3mm" || thickness === "hardcover_2mm") return "casewrap";
  const explicit = String(args.coverGeometryType || "");
  return explicit === "casewrap" ? "casewrap" : "perfect_binding";
}

export function normalizeCoverMeasure(value: unknown, fallback = 0) {
  const n = Number(value);
  return Number.isFinite(n) && n >= 0 ? n : fallback;
}

// English comment: Resolve full cover spread geometry for softcover and casewrap products.
export function resolveCoverSpreadGeometry(input: CoverGeometryInput): CoverSpreadGeometry {
  const panelWmm = Math.max(1, Number(input.panelWmm || 0));
  const panelHmm = Math.max(1, Number(input.panelHmm || 0));
  const spineMm = Math.max(0, Number(input.spineMm || 0));
  const type = input.coverGeometryType === "casewrap" ? "casewrap" : "perfect_binding";

  if (type !== "casewrap") {
    const artWmm = panelWmm * 2 + spineMm;
    const artHmm = panelHmm;
    const back = { x: 0, y: 0, w: panelWmm, h: panelHmm };
    const spine = { x: panelWmm, y: 0, w: spineMm, h: panelHmm };
    const front = { x: panelWmm + spineMm, y: 0, w: panelWmm, h: panelHmm };
    return {
      type,
      panelWmm,
      panelHmm,
      spineMm,
      wrapMm: 0,
      squeezeMm: 0,
      overlapMm: 0,
      edgeMm: 0,
      artWmm,
      artHmm,
      back,
      spine,
      front,
      wrapBox: null,
      contentBox: { x: 0, y: 0, w: artWmm, h: artHmm },
    };
  }

  const rawWrapMm = normalizeCoverMeasure(input.coverWrapMm, 0);
  const rawSqueezeMm = normalizeCoverMeasure(input.coverSqueezeMm, 0);
  const rawOverlapMm = normalizeCoverMeasure(input.coverOverlapMm, 0);
  const useDefaults = rawWrapMm <= 0 && rawSqueezeMm <= 0 && rawOverlapMm <= 0;
  const wrapMm = useDefaults ? 18 : rawWrapMm;
  const squeezeMm = useDefaults ? 5 : rawSqueezeMm;
  const overlapMm = useDefaults ? 3 : rawOverlapMm;
  const edgeMm = wrapMm + overlapMm;
  const artWmm = edgeMm + panelWmm + squeezeMm + spineMm + squeezeMm + panelWmm + edgeMm;
  const artHmm = edgeMm + panelHmm + edgeMm;
  const back = { x: edgeMm, y: edgeMm, w: panelWmm, h: panelHmm };
  const spine = { x: edgeMm + panelWmm + squeezeMm, y: edgeMm, w: spineMm, h: panelHmm };
  const front = { x: spine.x + spineMm + squeezeMm, y: edgeMm, w: panelWmm, h: panelHmm };

  return {
    type,
    panelWmm,
    panelHmm,
    spineMm,
    wrapMm,
    squeezeMm,
    overlapMm,
    edgeMm,
    artWmm,
    artHmm,
    back,
    spine,
    front,
    wrapBox: { x: 0, y: 0, w: artWmm, h: artHmm },
    contentBox: {
      x: edgeMm,
      y: edgeMm,
      w: artWmm - edgeMm * 2,
      h: panelHmm,
    },
  };
}
